import { useState, type FormEvent, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from 'convex/react';
import { X } from 'lucide-react';

import { api } from '../../../convex/_generated/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/components/ui/use-toast';
import { friendlyAuthError } from '@/lib/auth-errors';
import { ROUTES } from '@/lib/constants';
import { sectionNumber } from '@/lib/utils';

interface NewProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const MAX_TAGS = 8;

export function NewProjectDialog({ open, onOpenChange }: NewProjectDialogProps) {
  const { token } = useAuth();
  const create = useMutation(api.projects.create);
  const navigate = useNavigate();
  const { toast } = useToast();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tagDraft, setTagDraft] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setName('');
    setDescription('');
    setTagDraft('');
    setTags([]);
    setSubmitting(false);
  };

  const addTag = (raw: string) => {
    const t = raw.trim().toLowerCase();
    if (!t) return;
    if (tags.includes(t)) return;
    if (tags.length >= MAX_TAGS) return;
    if (t.length > 24) return;
    setTags([...tags, t]);
    setTagDraft('');
  };

  const onTagKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(tagDraft);
    } else if (e.key === 'Backspace' && tagDraft === '' && tags.length > 0) {
      setTags(tags.slice(0, -1));
    }
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!token) return;
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      const projectId = await create({
        token,
        name: name.trim(),
        description: description.trim() || undefined,
        tags,
      });
      // Pre-emptive close + navigate.
      reset();
      onOpenChange(false);
      navigate(ROUTES.projectWizard(projectId));
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Could not create project',
        description: friendlyAuthError(err),
      });
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o && !submitting) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <p className="marginalia">
            <span className="mr-2">{sectionNumber(0)}</span>new project
          </p>
          <DialogTitle className="italic">Name your project.</DialogTitle>
          <DialogDescription>
            A short title and a sentence about what you are building. You
            can change these later.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="grid gap-5">
          <div className="grid gap-2">
            <Label htmlFor="proj-name">Project name</Label>
            <Input
              id="proj-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              maxLength={100}
              autoFocus
              placeholder="My portfolio site"
              disabled={submitting}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="proj-desc">
              Description <span className="text-graphite text-xs">(optional)</span>
            </Label>
            <Textarea
              id="proj-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
              rows={3}
              placeholder="A personal portfolio for my photography. Quiet, editorial, and easy for clients to navigate."
              disabled={submitting}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="proj-tags">
              Tags <span className="text-graphite text-xs">(optional)</span>
            </Label>
            <Input
              id="proj-tags"
              value={tagDraft}
              onChange={(e) => setTagDraft(e.target.value)}
              onKeyDown={onTagKey}
              placeholder="Type a tag, press Enter"
              disabled={submitting || tags.length >= MAX_TAGS}
              maxLength={24}
            />
            {tags.length > 0 ? (
              <div className="mt-1 flex flex-wrap gap-1.5">
                {tags.map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center gap-1 rounded-sm border border-ink/20 bg-paper-2 px-2 py-0.5 font-mono text-[11px] uppercase tracking-wider text-ink"
                  >
                    {t}
                    <button
                      type="button"
                      onClick={() => setTags(tags.filter((x) => x !== t))}
                      className="text-graphite hover:text-vermilion"
                      aria-label={`Remove tag ${t}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            ) : null}
            <p className="font-mono text-[11px] text-graphite">
              <span className="text-ochre">¶</span>&nbsp;&nbsp;Up to {MAX_TAGS} tags. Comma or Enter to add.
            </p>
          </div>

          <DialogFooter className="mt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || submitting}>
              {submitting ? 'Creating…' : 'Create & continue'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
